import { Grid, IconButton, Tooltip, Paper, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  MdAdd,
  MdArrowBack,
  MdClose,
  MdPeople,
  MdTrendingUp,
  MdTrendingDown,
  MdAccountBalanceWallet,
} from 'react-icons/md'
import { GlobalContext } from '../../../../App'
import Programa from '../../../../components/Programa/Programa'
import PrimeTable from '../../../../components/PrimeTable'
import { metodoPrograma } from '../../../../components/api'
import Edicao from './Edicao'
import Visualiza from './Visualiza'
import ModalAberturaCaixa from './modals/ModalAberturaCaixa'
import ModalLancamentoCaixa from './modals/ModalLancamentoCaixa'
import ModalFechamentoCaixa from './modals/ModalFechamentoCaixa'
import { useModal } from '../../../../components/Utils/UseModal'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import { useParams } from 'react-router-dom'

dayjs.locale('pt-br')

const CardMetric = ({
  label,
  value,
  subtitle,
  icon,
  bgColor,
  borderColor,
  iconBgColor,
}) => (
  <Paper
    style={{
      borderRadius: 12,
      border: `1px solid ${borderColor}`,
      backgroundColor: bgColor,
      padding: '20px 24px',
      height: '100%',
    }}
  >
    <Grid container justifyContent="space-between" alignItems="flex-start">
      <Grid item>
        <Typography
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: '#616161',
            marginBottom: 8,
          }}
        >
          {label}
        </Typography>
        <Typography
          style={{
            fontWeight: 700,
            fontSize: 28,
            color: '#212121',
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            style={{
              fontSize: 12,
              color: '#9e9e9e',
              marginTop: 4,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Grid>
      <Grid item>
        <div
          style={{
            borderRadius: '50%',
            backgroundColor: iconBgColor,
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
      </Grid>
    </Grid>
  </Paper>
)

const formatCurrency = (v) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const initialPgn = {
  page: 0,
  first: 0,
  rows: 10,
  sortField: null,
  sortOrder: null,
}

const CaixaFechamento = () => {
  const { idRegistro } = useParams()

  const { localAtendimento } = useContext(GlobalContext)

  const [lista, setLista] = useState([])
  const [tela, setTela] = useState('lista')
  const [ferramentas, setFerramentas] = useState()
  const [dados, setDados] = useState(null)
  const [entradas, setEntradas] = useState(0.0)
  const [saidas, setSaidas] = useState(0.0)
  const [resultado, setResultado] = useState(0.0)
  const [dadosEdicao, setDadosEdicao] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [externalPagination, setExternalPagination] = useState(initialPgn)
  const modalAbertura = useModal(ModalAberturaCaixa)
  const modalLancamento = useModal(ModalLancamentoCaixa)
  const modalFechamento = useModal(ModalFechamentoCaixa)

  const caixasAbertos = useMemo(
    () => lista.filter((i) => i[6] === 'Aberto').length,
    [lista],
  )

  const caixasAbertosHoje = useMemo(
    () =>
      lista.filter((i) => {
        console.log(i)
        return false
      }).length,
    [lista],
  )

  const getList = useCallback(
    async (externalCnstPagination) => {
      const CnstPagination = externalCnstPagination || initialPgn
      const res = await metodoPrograma('lista', {
        idLocalAtendimento: localAtendimento[0],
        idRegistro,
        CnstPagination,
      })
      if (res.status) {
        setEntradas(res.valorEntrada ?? 0)
        setSaidas(res.valorSaida ?? 0)
        setResultado(res.totalGeral ?? 0)
        setLista(res.lista)
        setExternalPagination(res?.CnstPagination)
      }
    },
    [localAtendimento, idRegistro],
  )

  const voltarCaixa = () => {
    setTela('visualiza')
    setDadosEdicao(null)
  }

  const voltar = useCallback(() => {
    setTela('lista')
    setDados(null)
    setDadosEdicao(null)
  }, [])

  const visualizaCaixa = useCallback(async () => {
    setTela('visualiza')
    await getList()
  }, [getList])

  const handleClick = (linha) => {
    setDados(linha)
    setTela('visualiza')
  }

  const abreNovoCaixa = useCallback(async () => {
    const result = await modalAbertura({
      idLocalAtendimento: localAtendimento[0],
      idRegistro,
    })
    if (result) {
      await getList()
    }
  }, [localAtendimento, getList, modalAbertura, idRegistro])

  const abreLancamentoCaixa = useCallback(async () => {
    const result = await modalLancamento({
      idCaixa: dados[0],
      idRegistro,
      idLocalAtendimento: localAtendimento[0],
    })
    if (result) {
      setRefreshKey((prev) => prev + 1)
    }
  }, [dados, localAtendimento, modalLancamento, idRegistro])

  const abreFechamentoCaixa = useCallback(
    async (e, item) => {
      e.stopPropagation()
      e.preventDefault()
      const result = await modalFechamento({
        idCaixa: item[0],
        idRegistro,
        idLocalAtendimento: localAtendimento[0],
      })
      if (result) {
        await getList()
      }
    },
    [localAtendimento, getList, modalFechamento, idRegistro],
  )

  useEffect(() => {
    switch (tela) {
      case 'lista':
        setFerramentas(
          <Tooltip title="Abrir Caixa">
            <span>
              <IconButton onClick={() => abreNovoCaixa()}>
                <MdAdd />
              </IconButton>
            </span>
          </Tooltip>,
        )
        break
      case 'visualiza':
        setFerramentas(
          <>
            <Tooltip title="Adicionar Lançamento">
              <span>
                <IconButton onClick={abreLancamentoCaixa}>
                  <MdAdd />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Voltar">
              <IconButton onClick={voltar}>
                <MdArrowBack />
              </IconButton>
            </Tooltip>
          </>,
        )
        break
      case 'edicao':
        setFerramentas(
          <Tooltip title="Voltar">
            <span>
              <IconButton onClick={voltarCaixa}>
                <MdArrowBack />
              </IconButton>
            </span>
          </Tooltip>,
        )
        break
      default:
        break
    }
  }, [tela, voltar, abreNovoCaixa, abreLancamentoCaixa])

  useEffect(() => {
    if (localAtendimento) {
      getList()
    }
  }, [localAtendimento, getList])

  return (
    <Programa codigo="CtrlCaixaFechamento" ferramentas={ferramentas}>
      {tela === 'lista' ? (
        <>
          <Typography
            variant="h4"
            style={{ fontWeight: 700, color: '#212121', marginBottom: 2 }}
          >
            {idRegistro}) Caixa
          </Typography>
          <Typography
            style={{ fontSize: 14, color: '#9e9e9e', marginBottom: 16 }}
          >
            {dayjs().format('dddd, D [de] MMMM [de] YYYY')}
          </Typography>

          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6} md={3}>
              <CardMetric
                label="Caixas Abertos"
                value={caixasAbertos}
                subtitle={`${caixasAbertosHoje} abertos hoje`}
                icon={<MdPeople style={{ fontSize: 22, color: '#4caf50' }} />}
                bgColor="#e8f5e9"
                borderColor="#a5d6a7"
                iconBgColor="#c8e6c9"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardMetric
                label="Entradas Hoje"
                value={formatCurrency(entradas)}
                subtitle="Transações de entrada"
                icon={
                  <MdTrendingUp style={{ fontSize: 22, color: '#4caf50' }} />
                }
                bgColor="#e8f5e9"
                borderColor="#a5d6a7"
                iconBgColor="#c8e6c9"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardMetric
                label="Saídas Hoje"
                value={formatCurrency(saidas)}
                subtitle="Transações de saída"
                icon={
                  <MdTrendingDown style={{ fontSize: 22, color: '#e91e63' }} />
                }
                bgColor="#fce4ec"
                borderColor="#f48fb1"
                iconBgColor="#f8bbd0"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardMetric
                label="Balanço Hoje"
                value={formatCurrency(resultado)}
                icon={
                  <MdAccountBalanceWallet
                    style={{ fontSize: 22, color: '#757575' }}
                  />
                }
                bgColor="#fafafa"
                borderColor="#e0e0e0"
                iconBgColor="#f5f5f5"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PrimeTable
                dadosTable={lista}
                parametros={{ idRegistro }}
                externalPagination={externalPagination}
                setExternalPagination={async (v) => {
                  await getList(v)
                }}
                head={['Abertura', 'Entradas', 'Saídas', 'Saldo', 'Status', '']}
                line={(item) => [
                  item[2],
                  item[3],
                  item[4],
                  item[5],
                  item[6],
                  item[6],
                ]}
                collumnsDate={[0]}
                collumnsCurrency={[1, 2, 3]}
                columnOptions={[
                  {
                    alias: 5,
                    customJsx: ({ linhaOriginal }) =>
                      linhaOriginal[6] === 'Aberto' ? (
                        <IconButton
                          size="small"
                          title="Fechar Caixa"
                          onClick={(e) => {
                            e.stopPropagation()
                            abreFechamentoCaixa(e, linhaOriginal)
                          }}
                        >
                          <MdClose />
                        </IconButton>
                      ) : null,
                  },
                ]}
                onRowSelect={handleClick}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <div>
          {tela === 'visualiza' ? (
            <Visualiza
              {...{
                dados,
                setDadosEdicao,
                setTela,
                refreshKey,
                idRegistro,
              }}
            />
          ) : (
            <Edicao
              {...{
                dados,
                voltarCaixa,
                visualizaCaixa,
                dadosEdicao,
                idRegistro,
              }}
            />
          )}
        </div>
      )}
    </Programa>
  )
}

export default CaixaFechamento
